import React, { useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Box,
  Switch,
  useTheme,
  InputAdornment,
  IconButton,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { db } from "../../firebase"; // Adjust path based on your Firebase setup
import { ref, set } from "firebase/database";
import Notification from "../Notification";
import { useFormik } from "formik";
import * as yup from "yup";
import CustomTextField from "../Navigation/CustomTextField";
import useAppConfig from "../../Hooks/useAppConfig";

// Validation schema for creating a new application (all fields required)
const createValidationSchema = yup.object({
  name: yup.string().required("Application name is required"),
  icon: yup
    .string()
    .matches(
      /^(https?:\/\/.*\.(?:png|jpg|jpeg|svg|webp)|(\.\.\/)?.*\.(?:png|jpg|jpeg|svg|webp))$/i,
      "Icon must be a valid URL or path and an image file (.png, .jpg, .jpeg, .svg, .webp)"
    )
    .required("Icon URL or path is required"),
  downloadURL: yup
    .string()
    .url("Enter a valid download URL")
    .required("Download URL is required"),
  fileName: yup
    .string()
    .matches(
      /^[a-zA-Z0-9_.-]+\.(msi|exe)$/,
      "File name must be valid (e.g., 'file.msi' or 'file.exe')"
    )
    .required("File name and extension are required"),
  installParams: yup
    .string()
    .matches(/^[-/]/, "Installation parameters must start with '-' or '/'")
    .required("Installation parameters are required"),
});

// Validation schema for editing an application (fields optional)
const editValidationSchema = yup.object({
  name: yup.string().required("Application name is required"),
  icon: yup
    .string()
    .matches(
      /^(https?:\/\/.*\.(?:png|jpg|jpeg|svg)|(\.\.\/)?.*\.(?:png|jpg|jpeg|svg))$/i,
      "Icon must be a valid URL or path and an image file (.png, .jpg, .jpeg, .svg)"
    )
    .required("Icon URL or path is required"),
  downloadURL: yup.string().url("Enter a valid download URL").nullable(), // Optional when editing
  fileName: yup
    .string()
    .matches(
      /^[a-zA-Z0-9_.-]+\.(msi|exe)$/,
      "File name must be valid (e.g., 'file.msi' or 'file.exe')"
    )
    .nullable(), // Optional when editing
  installParams: yup
    .string()
    .matches(/^[-/]/, "Installation parameters must start with '-' or '/'")
    .nullable(), // Optional when editing
});


function ApplicationFormModal({
  open,
  onClose,
  appKey, // Pass appKey to indicate whether we are editing
  application,
  fetchApplications,
}) {
  const theme = useTheme();

  // Choose the validation schema based on whether appKey exists (edit mode vs create mode)
  const validationSchema = appKey ? editValidationSchema : createValidationSchema;
  const { configData, fetchConfig, loading } = useAppConfig(); // Initialize the hook

  // Initialize formik for form state and validation
  const formik = useFormik({
    initialValues: {
      name: application?.name || "",
      icon: application?.icon || "",
      defaultApp: application?.defaultApp || false,
      downloadURL: application?.downloadURL || "",
      installParams: application?.installParams || "",
      fileName: application?.fileName || "",
      confidenceLevel: 0,
    },
    validationSchema, // Use the dynamically selected schema
    onSubmit: (values) => {
      const appRef = ref(
        db,
        `Applications/${appKey || values.name.replace(/\s+/g, "")}`
      );
      set(appRef, values)
        .then(() => {
          Notification.success(
            theme,
            "Success",
            `Application ${appKey ? "updated" : "added"} successfully.`
          );
          handleCancel(); // Reset the form and close the modal
          fetchApplications(); // Refetch applications after submission
        })
        .catch((error) => {
          console.error("Error saving application:", error);
          Notification.error(
            theme,
            "Error",
            `There was a problem saving the application data. Error: ${error.message}`
          );
        });
    },
    enableReinitialize: true, // Reinitialize form when application data changes
  });

 
  // Create a ref for Formik
  const formikRef = useRef(formik);

  // Update the ref whenever Formik changes
  useEffect(() => {
    formikRef.current = formik;
  }, [formik]);

  // Generic helper function to update Formik form values
  const updateFormValues = (newValues) => {
    if (formikRef.current && newValues) {
      formikRef.current.setValues({
        ...formikRef.current.values,
        ...newValues,
      });
    }
  };

  // useEffect to update form values when 'application' changes
  useEffect(() => {
    if (application) {
      // Assuming 'application' has the same structure as Formik's form values
      updateFormValues(application);
    }
  }, [application]); // Only 'application' is a dependency

  // useEffect to update form values when 'configData' changes
  useEffect(() => {
    if (configData) {
      const newConfigValues = {
        downloadURL: configData.downloadURL || formikRef.current.values.downloadURL,
        installParams: configData.silentParams || formikRef.current.values.installParams,
        fileName: configData.installerName || formikRef.current.values.fileName,
        confidenceLevel: configData.confidenceLevel || formikRef.current.values.confidenceLevel,
        icon: configData.iconURL || formikRef.current.values.icon,
      };
      updateFormValues(newConfigValues);
    }
  }, [configData]); 


    // Reset the form when the modal is closed
    const handleCancel = () => {
      formik.resetForm(); // Reset form state using formik
      onClose();
    };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleCancel} // Call handleCancel on close
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          color: "white",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.info.main,
          padding: theme.spacing(2), // Add padding to the title
        }}
      >
        {appKey ? "Edit Application" : "Add New Application"}
      </DialogTitle>
      <DialogContent
        sx={{
          paddingX: theme.spacing(2), // Horizontal padding
          paddingTop: theme.spacing(3), // Top padding for the content
        }}
      >
        <Box
          sx={{
            marginBottom: theme.spacing(2),
            "& .MuiFormControl-root": {
              marginTop: theme.spacing(2), // Apply top margin for form controls like TextField
            },
          }}
        >
<Box sx={{ position: "relative", width: "100%", marginBottom: 2 }}>
  <CustomTextField
    label="Application Name"
    name="name"
    value={formik.values.name}
    onChange={formik.handleChange}
    formik={formik}
    helperText="Please enter the name of the application."
    disabled={!!appKey}
    InputProps={{
      style: { color: theme.palette.success.text, backgroundColor: "transparent" },
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            onClick={() => fetchConfig(formik.values.name)}
            disabled={!formik.values.name || loading}
          >
            <img src="openai.png" width="33px" alt="OpenAI icon" />
          </IconButton>
        </InputAdornment>
      ),
    }}
  />

  {formik.values.confidenceLevel > 1 && (
    <Tooltip title={"AI Confidence Level: " + formik.values.confidenceLevel} >
    <LinearProgress
      variant="determinate"
      value={formik.values.confidenceLevel}
      sx={{
        marginLeft: "8px",
        marginRight: "12px",
        position: "absolute",
        bottom: 0, // Aligns it with the bottom of the TextField
        left: 0,
        width: "97%",
        height: "5px", // Adjust height to make it look like a border
        backgroundColor: theme.palette.success.main,
        "& .MuiLinearProgress-bar": {
          backgroundColor: "#10a982",
        },
      }}
    />
    </Tooltip>
  )}
</Box>

          <CustomTextField
            label="Icon URL or Path"
            name="icon"
            value={formik.values.icon}
            onChange={formik.handleChange}
            formik={formik}
            helperText="Enter the URL or path of the icon (e.g., 'https://example.com/icon.png' or 'vscode.png')"
          />

          <CustomTextField
            label="Download URL"
            name="downloadURL"
            value={formik.values.downloadURL}
            onChange={formik.handleChange}
            formik={formik}
            helperText="Enter the direct download URL for the application installer"
          />

          <CustomTextField
            label="File Name and Extension"
            name="fileName"
            value={formik.values.fileName}
            onChange={formik.handleChange}
            formik={formik}
            helperText="Enter the name of the file to download including the extension, i.e CitrixInstaller.msi"
          />

          <CustomTextField
            label="Installation Parameters"
            name="installParams"
            value={formik.values.installParams}
            onChange={formik.handleChange}
            formik={formik}
            helperText="Enter the installation parameters (e.g., '/qn /norestart')"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formik.values.defaultApp}
                onChange={formik.handleChange}
                name="defaultApp"
                color="primary"
              />
            }
            label="Default Application"
            sx={{
              color: "white",
              marginLeft: theme.spacing(1),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          padding: theme.spacing(2), // Add padding around the action buttons
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Button onClick={handleCancel} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={formik.handleSubmit} // Use formik's submit handler
          variant="contained"
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.info.main,
          }}
        >
          {appKey ? "Save Changes" : "Add Application"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

export default ApplicationFormModal;
